import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import { Problem } from "../model/problem";
import { getErrorMessage } from "../utils/getErrorMessage";

/**
 * Entities the delete confirmation has a sentence for; mirrors the keys under
 * `confirm.delete` in the translation bundles. Typed as a union so a typo is a
 * compile error rather than a dialog showing a raw i18n key.
 */
export type DeletableEntityKey =
    | "activity"
    | "caretaker"
    | "doctor"
    | "familyMember"
    | "measurementType"
    | "measurementValueType"
    | "organization"
    | "organizationAdmin"
    | "patient"
    | "treatment";

interface CrudModalsProps {
    /** Current slice status; drives which modal, if any, is open. */
    status: string;
    error?: string | Problem;
    /**
     * Which entity is being deleted. The confirmation is one whole sentence per
     * entity rather than a shared sentence with the noun interpolated:
     * languages that inflect the noun (Russian and Kazakh both do here) cannot
     * be translated correctly from a template.
     */
    entityKey: DeletableEntityKey;
    /** Label of the record about to be deleted. */
    deleteTarget?: string | null;
    /** Clears the slice error / cancels the pending delete. */
    onDismiss: () => void;
    onConfirmDelete: () => void;
}

/**
 * The error dialog and delete confirmation shared by the entity list pages.
 *
 * The confirmation is titled and marked, and its confirm button is styled as
 * destructive with Cancel as the default — the dialog used to be an untitled
 * sentence with two equally weighted Yes/No buttons, which is thin ceremony for
 * an irreversible delete.
 *
 * Dismissing the error modal always clears the slice error — the pages used to
 * pass an empty `onOk` here, which left the dialog permanently stuck open.
 */
export const CrudModals = ({
    status,
    error,
    entityKey,
    deleteTarget,
    onDismiss,
    onConfirmDelete
}: CrudModalsProps) => {
    const { t } = useTranslation();
    const errorMessage = getErrorMessage(error);

    return (
        <>
            <Modal
                title={errorMessage.title}
                visible={status === "error" || status === "auth-error"}
                onOk={onDismiss}
                onCancel={onDismiss}
                cancelButtonProps={{ hidden: true }}
            >
                <p>{errorMessage.details}</p>
            </Modal>
            <Modal
                visible={status === "deleting"}
                okText={t("actions.delete")}
                cancelText={t("actions.cancel")}
                okButtonProps={{ danger: true }}
                onCancel={onDismiss}
                onOk={onConfirmDelete}
                width={460}
                closable={false}
            >
                <div className="confirm">
                    <span className="confirm__mark">
                        <ExclamationCircleOutlined />
                    </span>
                    <div>
                        <div className="confirm__title">{t("confirm.deleteTitle")}</div>
                        <div className="confirm__body">
                            {t(`confirm.delete.${entityKey}`, { name: deleteTarget })}
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};
